import React, { useState, useEffect, useRef, useMemo } from 'react';
import { customerService } from '../services/customerService';
import BaseModal from './common/BaseModal';
import api from '../services/api';
import useFormValidation from '../hooks/useFormValidation';
import { validators, normalizeNip } from '../utils/validation';

// Lista krajów z kodami kierunkowymi
const COUNTRIES = [
  { code: 'PL', name: 'Polska', phoneCode: '+48', flag: '🇵🇱' },
  { code: 'DE', name: 'Niemcy', phoneCode: '+49', flag: '🇩🇪' },
  { code: 'GB', name: 'Wielka Brytania', phoneCode: '+44', flag: '🇬🇧' },
  { code: 'US', name: 'USA', phoneCode: '+1', flag: '🇺🇸' },
  { code: 'FR', name: 'Francja', phoneCode: '+33', flag: '🇫🇷' },
  { code: 'IT', name: 'Włochy', phoneCode: '+39', flag: '🇮🇹' },
  { code: 'ES', name: 'Hiszpania', phoneCode: '+34', flag: '🇪🇸' },
  { code: 'CZ', name: 'Czechy', phoneCode: '+420', flag: '🇨🇿' },
  { code: 'SK', name: 'Słowacja', phoneCode: '+421', flag: '🇸🇰' },
  { code: 'UA', name: 'Ukraina', phoneCode: '+380', flag: '🇺🇦' },
  { code: 'LT', name: 'Litwa', phoneCode: '+370', flag: '🇱🇹' },
  { code: 'NL', name: 'Holandia', phoneCode: '+31', flag: '🇳🇱' },
  { code: 'BE', name: 'Belgia', phoneCode: '+32', flag: '🇧🇪' },
  { code: 'AT', name: 'Austria', phoneCode: '+43', flag: '🇦🇹' },
  { code: 'CH', name: 'Szwajcaria', phoneCode: '+41', flag: '🇨🇭' },
  { code: 'SE', name: 'Szwecja', phoneCode: '+46', flag: '🇸🇪' },
  { code: 'NO', name: 'Norwegia', phoneCode: '+47', flag: '🇳🇴' },
  { code: 'DK', name: 'Dania', phoneCode: '+45', flag: '🇩🇰' },
  { code: 'FI', name: 'Finlandia', phoneCode: '+358', flag: '🇫🇮' },
  { code: 'IE', name: 'Irlandia', phoneCode: '+353', flag: '🇮🇪' },
  { code: 'PT', name: 'Portugalia', phoneCode: '+351', flag: '🇵🇹' },
  { code: 'GR', name: 'Grecja', phoneCode: '+30', flag: '🇬🇷' },
  { code: 'RO', name: 'Rumunia', phoneCode: '+40', flag: '🇷🇴' },
  { code: 'HU', name: 'Węgry', phoneCode: '+36', flag: '🇭🇺' },
  { code: 'BG', name: 'Bułgaria', phoneCode: '+359', flag: '🇧🇬' },
  { code: 'HR', name: 'Chorwacja', phoneCode: '+385', flag: '🇭🇷' },
  { code: 'SI', name: 'Słowenia', phoneCode: '+386', flag: '🇸🇮' },
  { code: 'RS', name: 'Serbia', phoneCode: '+381', flag: '🇷🇸' },
  { code: 'BY', name: 'Białoruś', phoneCode: '+375', flag: '🇧🇾' },
  { code: 'RU', name: 'Rosja', phoneCode: '+7', flag: '🇷🇺' },
  { code: 'TR', name: 'Turcja', phoneCode: '+90', flag: '🇹🇷' },
  { code: 'CN', name: 'Chiny', phoneCode: '+86', flag: '🇨🇳' },
  { code: 'JP', name: 'Japonia', phoneCode: '+81', flag: '🇯🇵' },
  { code: 'KR', name: 'Korea Płd.', phoneCode: '+82', flag: '🇰🇷' },
  { code: 'AU', name: 'Australia', phoneCode: '+61', flag: '🇦🇺' },
  { code: 'CA', name: 'Kanada', phoneCode: '+1', flag: '🇨🇦' },
  { code: 'MX', name: 'Meksyk', phoneCode: '+52', flag: '🇲🇽' },
  { code: 'BR', name: 'Brazylia', phoneCode: '+55', flag: '🇧🇷' },
  { code: 'AR', name: 'Argentyna', phoneCode: '+54', flag: '🇦🇷' },
];

const fieldErrorStyle = { fontSize: '10px', color: 'var(--danger)', marginTop: '2px' };

const inputStyle = {
  width: '100%', padding: '0.4rem 0.6rem', fontSize: '11px',
  border: '1px solid var(--border)', borderRadius: '4px', outline: 'none'
};

const inputErrorStyle = {
  ...inputStyle,
  border: '1px solid var(--danger)'
};

const labelStyle = { fontSize: '10px', color: 'var(--text-muted)', marginBottom: '2px', display: 'block' };

const checkboxLabelStyle = {
  display: 'flex', alignItems: 'center', gap: '4px',
  fontSize: '11px', color: 'var(--text-secondary)', cursor: 'pointer'
};

const checkboxStyle = { width: '14px', height: '14px', cursor: 'pointer', margin: 0 };

// Nazwa karty wskazanej przez backend przy konflikcie NIP
// Nazwa karty wskazanej w odpowiedzi 409 / na liscie kandydatow.
// KOLEJNOSC JEST ISTOTNA: backend sklada te obiekty w kontrahenci_service._kandydat
// i api/customers.add_customer, gdzie nazwa siedzi w polu 'nazwa' (a NIE w
// 'nazwa_firmy'). Bez tego pola dialog konfliktu NIP pokazywal samo '#12'.
const conflictName = (c) => {
  if (!c) return '';
  return c.nazwa || c.nazwa_firmy || `${c.imie || ''} ${c.nazwisko || ''}`.trim() || `#${c.id}`;
};

const getInitialCustomer = (initialData = {}) => ({
  typ_klienta: 'osoba_fizyczna',
  imie: '', nazwisko: '', email: '',
  telefon_kod: '+48', telefon: '',
  adres: '', miasto: '', kod_pocztowy: '', kraj: 'Polska', kraj_kod: 'PL',
  nip: '', regon: '', nazwa_firmy: '', uwagi: '',
  jest_odbiorca: 1, jest_dostawca: 0,
  zgoda_sms: 0, zgoda_email: 0,
  aktywny: 1,
  wysylka_inna: false, wysylka_imie: '', wysylka_nazwisko: '', wysylka_firma: '',
  wysylka_ulica: '', wysylka_kod_pocztowy: '', wysylka_miasto: '',
  wysylka_kraj: 'Polska', wysylka_kraj_kod: 'PL',
  wysylka_telefon_kod: '+48', wysylka_telefon: '',
  karta_lojalnosciowa_1: '', karta_lojalnosciowa_2: '',
  ...initialData
});

// Data nadania zgody - podpis pod checkboxem
const formatConsentDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('pl-PL');
};

// Wyciagnij karte wskazana przez backend w odpowiedzi 409 (duplikat NIP)
const extractConflictCustomer = (body) => {
  if (!body) return null;
  return body.existing || body.istniejacy || body.klient || body.customer
    || (body.data && !Array.isArray(body.data) ? body.data : null);
};

/**
 * Uniwersalny modal klienta z integracją GUS
 *
 * Tryby użycia:
 * 1. CustomersPage (controlled): isEdit, customer, setCustomer, onSubmit, onClose
 * 2. POS/Orders (standalone): isOpen, onSuccess, onClose, initialData
 */
const CustomerModal = ({
  // Tryb 1: CustomersPage (controlled)
  isEdit,
  customer: controlledCustomer,
  setCustomer: setControlledCustomer,
  onSubmit: controlledOnSubmit,

  // Tryb 2: POS/Orders (standalone)
  isOpen,
  onSuccess,
  initialData,

  // Konflikt NIP (409) - otwarcie istniejacej karty wskazanej przez backend
  onOpenExisting,

  // Wspólne
  onClose
}) => {
  // Określ tryb pracy
  const isControlledMode = controlledCustomer !== undefined && setControlledCustomer !== undefined;

  // Wewnętrzny state dla trybu standalone
  const [internalCustomer, setInternalCustomer] = useState(() => getInitialCustomer(initialData));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [conflict, setConflict] = useState(null);
  const prevIsOpen = useRef(false);
  const pendingForce = useRef(false);

  // GUS state
  const [gusLoading, setGusLoading] = useState(false);
  const [gusError, setGusError] = useState(null);
  const [vatStatus, setVatStatus] = useState(null);

  // Validation rules — built dynamically based on customer type
  const validationRules = useMemo(() => {
    const c = isControlledMode ? controlledCustomer : internalCustomer;
    const type = c?.typ_klienta || 'osoba_fizyczna';
    const rules = {};
    if (type === 'osoba_fizyczna') {
      rules.imie = [(v) => validators.required(v, 'Imię')];
      rules.nazwisko = [(v) => validators.required(v, 'Nazwisko')];
    }
    if (type === 'firma') {
      rules.nazwa_firmy = [(v) => validators.required(v, 'Nazwa firmy')];
      rules.nip = [(v) => v ? validators.nip(v) : null];
    }
    rules.email = [(v) => validators.email(v)];
    rules.telefon = [(v) => validators.phone(v)];
    return rules;
  }, [isControlledMode ? controlledCustomer?.typ_klienta : internalCustomer?.typ_klienta]);

  const { errors: fieldErrors, validate, clearError } = useFormValidation(validationRules);

  // Reset przy otwarciu (tryb standalone)
  useEffect(() => {
    if (!isControlledMode && isOpen && !prevIsOpen.current) {
      setInternalCustomer(getInitialCustomer(initialData));
      setError('');
      setConflict(null);
      setGusError(null);
      setVatStatus(null);
    }
    prevIsOpen.current = isOpen;
  }, [isOpen, isControlledMode, initialData]);

  // Określ dane klienta i funkcję aktualizacji
  const customer = isControlledMode ? controlledCustomer : internalCustomer;
  const setCustomer = isControlledMode ? setControlledCustomer : setInternalCustomer;

  // Ref na najswiezsza funkcje zapisu. Efekt ponizej musi stac PRZED
  // warunkowym returnem (zasady hookow), a submitCustomer jest nizej.
  const submitRef = useRef(null);

  // Ponowienie zapisu po kliknieciu "Utworz mimo to" - dopiero gdy flaga
  // force trafi juz do stanu (w trybie controlled payload buduje rodzic).
  useEffect(() => {
    if (pendingForce.current && customer && customer.force) {
      pendingForce.current = false;
      if (submitRef.current) submitRef.current(customer, null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer]);

  // Sprawdź czy modal powinien być widoczny
  if (!isControlledMode && !isOpen) return null;

  const handleGusLookup = async () => {
    const nip = customer.nip;
    if (!nip || nip.replace(/[\s-]/g, '').length < 10) {
      setGusError('Wprowadź poprawny numer NIP (10 cyfr)');
      return;
    }

    setGusLoading(true);
    setGusError(null);
    setVatStatus(null);

    try {
      const cleanNip = nip.replace(/[\s-]/g, '');
      const response = await api.get(`/gus/search/nip/${cleanNip}`);
      const data = response.data;

      if (data.success && data.data) {
        const gusData = data.data;

        setCustomer({
          ...customer,
          nazwa_firmy: gusData.nazwa || customer.nazwa_firmy,
          nip: gusData.nip || customer.nip,
          regon: gusData.regon || customer.regon,
          adres: gusData.adres || customer.adres,
          miasto: gusData.miasto || customer.miasto,
          kod_pocztowy: gusData.kod_pocztowy || customer.kod_pocztowy,
        });

        if (data.vat_status) {
          setVatStatus(data.vat_status);
        }

        setGusError(null);
      } else {
        setGusError(data.message || 'Nie znaleziono podmiotu');
      }
    } catch (err) {
      setGusError('Błąd połączenia z GUS: ' + err.message);
    } finally {
      setGusLoading(false);
    }
  };

  // Rozpoznaj konflikt NIP (409) - to nie jest blad krytyczny, tylko decyzja uzytkownika
  const handleSaveError = (err) => {
    if (err && err.status === 409) {
      const body = err.data || {};
      setConflict({
        existing: extractConflictCustomer(body),
        message: body.message || err.message || ''
      });
      return;
    }
    setError(err?.data?.message || err?.message || 'Błąd podczas zapisywania klienta');
  };

  // Wspolna sciezka zapisu - uzywana zarowno przez submit formularza,
  // jak i przez ponowienie z flaga force po konflikcie NIP.
  const submitCustomer = async (data, e) => {
    if (loading) return;
    if (!validate(data)) return;

    setError('');
    setLoading(true);
    try {
      if (isControlledMode && controlledOnSubmit) {
        // Tryb controlled - handler rodzica buduje payload i sam odswieza liste.
        // Rodzic MUSI przepuscic blad dalej, zebysmy zlapali tutaj 409.
        await controlledOnSubmit(e);
        return;
      }

      // Tryb standalone - zapisz przez API.
      // UWAGA: NIE wysylamy location_id - kartoteka jest wspolna dla sieci,
      // a wysylanie lokalizacji przestemplowywalo karte na sklep edytujacego.
      const customerData = {
        ...data,
        telefon: data.telefon ? `${data.telefon_kod || '+48'}${String(data.telefon).replace(/^\+\d+/, '')}` : '',
        wysylka_telefon: data.wysylka_telefon ? `${data.wysylka_telefon_kod || '+48'}${String(data.wysylka_telefon).replace(/^\+\d+/, '')}` : '',
        // NIP zapisujemy jako same cyfry (kreski/spacje z formularza odrzucamy),
        // zeby kasa, drukarka fiskalna i kopia paragonu dostaly ten sam ciag.
        nip: normalizeNip(data.nip),
        regon: data.regon || '',
        uwagi: data.uwagi || '',
        jest_odbiorca: Number(data.jest_odbiorca) ? 1 : 0,
        jest_dostawca: Number(data.jest_dostawca) ? 1 : 0,
        zgoda_sms: Number(data.zgoda_sms) ? 1 : 0,
        zgoda_email: Number(data.zgoda_email) ? 1 : 0,
        aktywny: data.aktywny === undefined ? 1 : (Number(data.aktywny) ? 1 : 0),
        karta_lojalnosciowa_1: data.karta_lojalnosciowa_1 || '',
        karta_lojalnosciowa_2: data.karta_lojalnosciowa_2 || ''
      };
      delete customerData.location_id;

      let savedCustomer;
      const customerId = data.id || initialData?.id;
      if (customerId) {
        savedCustomer = await customerService.updateCustomer(customerId, customerData);
      } else {
        savedCustomer = await customerService.createCustomer(customerData);
      }

      onSuccess && onSuccess(savedCustomer);
      onClose();
    } catch (err) {
      handleSaveError(err);
    } finally {
      setLoading(false);
    }
  };

  submitRef.current = submitCustomer;

  const handleSubmit = (e) => {
    e.preventDefault();
    setConflict(null);
    submitCustomer(customer, e);
  };

  // "Utworz mimo to" - ustawiamy flage force w danych i ponawiamy zapis.
  // W trybie controlled payload buduje rodzic, wiec flaga musi najpierw
  // trafic do stanu; ponowienie odpalamy w efekcie ponizej.
  const handleForceCreate = () => {
    setConflict(null);
    pendingForce.current = true;
    setCustomer({ ...customer, force: true });
  };

  const handleOpenExistingClick = () => {
    const existing = conflict?.existing;
    if (!existing) return;
    setConflict(null);
    if (onOpenExisting) {
      onOpenExisting(existing);
      return;
    }
    // Fallback (POS / wysylka): potraktuj istniejaca karte jak wybranego kontrahenta
    onSuccess && onSuccess(existing);
    onClose();
  };

  const footerButtons = (
    <>
      <button type="button" onClick={onClose} style={{
        padding: '0.5rem 1rem', fontSize: '11px', borderRadius: '4px',
        cursor: 'pointer', backgroundColor: 'var(--text-muted)', color: 'white'
      }}>Anuluj</button>
      <button type="submit" form="customer-form" disabled={loading} style={{
        padding: '0.5rem 1rem', fontSize: '11px', borderRadius: '4px',
        cursor: loading ? 'not-allowed' : 'pointer',
        backgroundColor: loading ? 'var(--text-muted)' : 'var(--success)',
        color: '#fff' , fontWeight: '600',
        opacity: loading ? 0.7 : 1
      }}>
        {loading ? '⏳ Zapisywanie...' : (isEdit ? '💾 Zapisz' : '➕ Dodaj')}
      </button>
    </>
  );

  return (
    <BaseModal
      isOpen={isControlledMode || isOpen}
      onClose={onClose}
      title={isEdit ? '✏️ Edytuj klienta' : '➕ Nowy klient'}
      size="md"
      footer={footerButtons}
    >
      <form id="customer-form" onSubmit={handleSubmit}>
          {/* Error message */}
          {error && (
            <div style={{
              background: 'var(--danger-light)',
              color: 'var(--danger)',
              border: '1px solid var(--danger-light)',
              borderRadius: '4px',
              padding: '0.5rem',
              marginBottom: '0.75rem',
              fontSize: '11px',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <span>⚠️</span> {error}
            </div>
          )}

          {/* Konflikt NIP (409) - to nie jest blad krytyczny, tylko decyzja uzytkownika */}
          {conflict && (
            <div style={{
              background: 'var(--warning-light)',
              color: 'var(--warning)',
              border: '1px solid var(--warning)',
              borderRadius: '4px',
              padding: '0.5rem',
              marginBottom: '0.75rem',
              fontSize: '11px'
            }}>
              <div style={{ marginBottom: '0.5rem' }}>
                ⚠️ Kontrahent o tym NIP już istnieje
                {conflict.existing ? (
                  <>: <strong>{conflictName(conflict.existing)}</strong>
                    {conflict.existing.numer_klienta ? ` (nr ${conflict.existing.numer_klienta})` : ''}
                  </>
                ) : '.'}
              </div>
              {conflict.message && (
                <div style={{ fontSize: '10px', marginBottom: '0.5rem', color: 'var(--text-muted)' }}>
                  {conflict.message}
                </div>
              )}
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {conflict.existing && (
                  <button type="button" onClick={handleOpenExistingClick} style={{
                    padding: '4px 10px', fontSize: '11px', borderRadius: '4px', border: 'none',
                    cursor: 'pointer', fontWeight: '600',
                    backgroundColor: 'var(--info)', color: '#fff'
                  }}>
                    Otwórz istniejącą kartę
                  </button>
                )}
                <button type="button" onClick={handleForceCreate} style={{
                  padding: '4px 10px', fontSize: '11px', borderRadius: '4px',
                  cursor: 'pointer', fontWeight: '500',
                  border: '1px solid var(--warning)',
                  backgroundColor: 'var(--card-bg)', color: 'var(--warning)'
                }}>
                  Utwórz mimo to
                </button>
                <button type="button" onClick={() => setConflict(null)} style={{
                  padding: '4px 10px', fontSize: '11px', borderRadius: '4px',
                  cursor: 'pointer', fontWeight: '500',
                  border: '1px solid var(--border)',
                  backgroundColor: 'var(--card-bg)', color: 'var(--text-secondary)'
                }}>
                  Popraw dane
                </button>
              </div>
            </div>
          )}

          {/* Role kontrahenta - niezalezne flagi, jeden podmiot moze miec obie */}
          <div style={{
            marginBottom: '0.75rem', padding: '0.5rem',
            backgroundColor: 'var(--bg)', borderRadius: '4px', border: '1px solid var(--border)'
          }}>
            <label style={labelStyle}>Rola w kartotece</label>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <label style={checkboxLabelStyle}>
                <input
                  type="checkbox"
                  checked={!!Number(customer.jest_odbiorca)}
                  onChange={e => setCustomer({ ...customer, jest_odbiorca: e.target.checked ? 1 : 0 })}
                  style={checkboxStyle}
                />
                Odbiorca (klient)
              </label>
              <label style={checkboxLabelStyle}>
                <input
                  type="checkbox"
                  checked={!!Number(customer.jest_dostawca)}
                  onChange={e => setCustomer({ ...customer, jest_dostawca: e.target.checked ? 1 : 0 })}
                  style={checkboxStyle}
                />
                Dostawca
              </label>
              <label style={checkboxLabelStyle}>
                <input
                  type="checkbox"
                  checked={customer.aktywny === undefined ? true : !!Number(customer.aktywny)}
                  onChange={e => setCustomer({ ...customer, aktywny: e.target.checked ? 1 : 0 })}
                  style={checkboxStyle}
                />
                Karta aktywna
              </label>
            </div>
            <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '4px' }}>
              Kartoteka jest wspólna dla całej sieci — karta nie jest przypisana do sklepu.
            </div>
          </div>

          {/* Zgody marketingowe - SMS i e-mail rozdzielone */}
          <div style={{
            marginBottom: '0.75rem', padding: '0.5rem',
            backgroundColor: 'var(--bg)', borderRadius: '4px', border: '1px solid var(--border)'
          }}>
            <label style={labelStyle}>Zgody marketingowe</label>
            <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
              <div>
                <label style={checkboxLabelStyle}>
                  <input
                    type="checkbox"
                    checked={!!Number(customer.zgoda_sms)}
                    onChange={e => setCustomer({ ...customer, zgoda_sms: e.target.checked ? 1 : 0 })}
                    style={checkboxStyle}
                  />
                  Zgoda na SMS
                </label>
                <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>
                  {customer.zgoda_sms_at
                    ? `Nadana: ${formatConsentDate(customer.zgoda_sms_at)}`
                    : 'Brak daty nadania'}
                </div>
              </div>
              <div>
                <label style={checkboxLabelStyle}>
                  <input
                    type="checkbox"
                    checked={!!Number(customer.zgoda_email)}
                    onChange={e => setCustomer({ ...customer, zgoda_email: e.target.checked ? 1 : 0 })}
                    style={checkboxStyle}
                  />
                  Zgoda na e-mail
                </label>
                <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>
                  {customer.zgoda_email_at
                    ? `Nadana: ${formatConsentDate(customer.zgoda_email_at)}`
                    : 'Brak daty nadania'}
                </div>
              </div>
            </div>
          </div>

          {/* Typ klienta */}
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={labelStyle}>Typ klienta</label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {['osoba_fizyczna', 'firma'].map(typ => (
                <button key={typ} type="button" onClick={() => setCustomer({...customer, typ_klienta: typ})}
                  style={{
                    flex: 1, padding: '0.4rem', fontSize: '11px', borderRadius: '4px',
                    cursor: 'pointer', fontWeight: '500',
                    backgroundColor: customer.typ_klienta === typ ? 'var(--primary)' : 'var(--border)',
                    color: customer.typ_klienta === typ ? '#fff' : 'var(--text-secondary)'
                  }}>
                  {typ === 'osoba_fizyczna' ? '👤 Osoba' : '🏢 Firma'}
                </button>
              ))}
            </div>
          </div>

          {/* Pola formularza */}
          {customer.typ_klienta === 'firma' && (
            <div style={{ marginBottom: '0.5rem' }}>
              <label style={labelStyle}>Nazwa firmy *</label>
              <input style={fieldErrors.nazwa_firmy ? inputErrorStyle : inputStyle} value={customer.nazwa_firmy || ''}
                onChange={e => { setCustomer({...customer, nazwa_firmy: e.target.value}); clearError('nazwa_firmy'); }} />
              {fieldErrors.nazwa_firmy && <div style={fieldErrorStyle}>{fieldErrors.nazwa_firmy}</div>}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <div>
              <label style={labelStyle}>Imię {customer.typ_klienta === 'osoba_fizyczna' && '*'}</label>
              <input style={fieldErrors.imie ? inputErrorStyle : inputStyle} value={customer.imie || ''}
                onChange={e => { setCustomer({...customer, imie: e.target.value}); clearError('imie'); }} />
              {fieldErrors.imie && <div style={fieldErrorStyle}>{fieldErrors.imie}</div>}
            </div>
            <div>
              <label style={labelStyle}>Nazwisko {customer.typ_klienta === 'osoba_fizyczna' && '*'}</label>
              <input style={fieldErrors.nazwisko ? inputErrorStyle : inputStyle} value={customer.nazwisko || ''}
                onChange={e => { setCustomer({...customer, nazwisko: e.target.value}); clearError('nazwisko'); }} />
              {fieldErrors.nazwisko && <div style={fieldErrorStyle}>{fieldErrors.nazwisko}</div>}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <div>
              <label style={labelStyle}>📧 Email</label>
              <input style={fieldErrors.email ? inputErrorStyle : inputStyle} type="email" value={customer.email || ''}
                onChange={e => { setCustomer({...customer, email: e.target.value}); clearError('email'); }} />
              {fieldErrors.email && <div style={fieldErrorStyle}>{fieldErrors.email}</div>}
            </div>
            <div>
              <label style={labelStyle}>📱 Telefon</label>
              <div style={{ display: 'flex', gap: '4px' }}>
                <select
                  style={{ ...inputStyle, width: '90px', flexShrink: 0 }}
                  value={customer.telefon_kod || '+48'}
                  onChange={e => setCustomer({...customer, telefon_kod: e.target.value})}
                >
                  {COUNTRIES.map(c => (
                    <option key={c.code} value={c.phoneCode}>{c.flag} {c.phoneCode}</option>
                  ))}
                </select>
                <input
                  style={{ ...(fieldErrors.telefon ? inputErrorStyle : inputStyle), flex: 1 }}
                  value={(customer.telefon || '').replace(/^\+\d+/, '')}
                  onChange={e => { setCustomer({...customer, telefon: e.target.value.replace(/[^\d]/g, '')}); clearError('telefon'); }}
                  placeholder="123456789"
                />
              </div>
              {fieldErrors.telefon && <div style={fieldErrorStyle}>{fieldErrors.telefon}</div>}
            </div>
          </div>

          {customer.typ_klienta === 'firma' && (
            <div style={{ marginBottom: '0.5rem' }}>
              <label style={labelStyle}>NIP</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input style={{ ...(fieldErrors.nip ? inputErrorStyle : inputStyle), flex: 1 }} value={customer.nip || ''}
                  onChange={e => { setCustomer({...customer, nip: e.target.value}); clearError('nip'); }}
                  onBlur={e => {
                    // Po wyjsciu z pola zostaja same cyfry ('526-025-02-74' -> '5260250274').
                    const cyfry = normalizeNip(e.target.value);
                    if (cyfry !== e.target.value) setCustomer({...customer, nip: cyfry});
                  }}
                  placeholder="np. 5260000238" />
                <button
                  type="button"
                  onClick={handleGusLookup}
                  disabled={gusLoading}
                  style={{
                    padding: '0.4rem 0.75rem',
                    backgroundColor: gusLoading ? 'var(--text-muted)' : 'var(--info)',
                    color: '#fff' ,
                    borderRadius: '4px',
                    cursor: gusLoading ? 'not-allowed' : 'pointer',
                    fontSize: '11px',
                    fontWeight: '500',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {gusLoading ? '⏳...' : '🏛️ GUS'}
                </button>
              </div>
              {gusError && (
                <div style={{ fontSize: '10px', color: 'var(--danger)', marginTop: '4px' }}>
                  ⚠️ {gusError}
                </div>
              )}
              {vatStatus && (
                <div style={{
                  fontSize: '10px',
                  marginTop: '4px',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  backgroundColor: vatStatus.active ? 'var(--success-light)' : 'var(--danger-light)',
                  color: vatStatus.active ? 'var(--success)' : 'var(--danger)'
                }}>
                  {vatStatus.active ? '✅ Czynny podatnik VAT' : `❌ ${vatStatus.status}`}
                </div>
              )}
              {fieldErrors.nip && <div style={fieldErrorStyle}>{fieldErrors.nip}</div>}
            </div>
          )}

          {customer.typ_klienta === 'firma' && (
            <div style={{ marginBottom: '0.5rem' }}>
              <label style={labelStyle}>REGON</label>
              <input style={inputStyle} value={customer.regon || ''}
                onChange={e => setCustomer({...customer, regon: e.target.value})}
                placeholder="np. 012345678" />
            </div>
          )}

          <div style={{ marginBottom: '0.5rem' }}>
            <label style={labelStyle}>📍 Adres</label>
            <input style={inputStyle} value={customer.adres || ''}
              onChange={e => setCustomer({...customer, adres: e.target.value})} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <div>
              <label style={labelStyle}>Miasto</label>
              <input style={inputStyle} value={customer.miasto || ''}
                onChange={e => setCustomer({...customer, miasto: e.target.value})} />
            </div>
            <div>
              <label style={labelStyle}>Kod pocztowy</label>
              <input style={inputStyle} value={customer.kod_pocztowy || ''}
                onChange={e => setCustomer({...customer, kod_pocztowy: e.target.value})} />
            </div>
          </div>

          <div style={{ marginBottom: '0.75rem' }}>
            <label style={labelStyle}>🌍 Kraj</label>
            <select
              style={inputStyle}
              value={customer.kraj || 'Polska'}
              onChange={e => {
                const selectedCountry = COUNTRIES.find(c => c.name === e.target.value);
                setCustomer({
                  ...customer,
                  kraj: e.target.value,
                  kraj_kod: selectedCountry?.code || 'PL'
                });
              }}
            >
              {COUNTRIES.map(c => (
                <option key={c.code} value={c.name}>{c.flag} {c.name}</option>
              ))}
            </select>
          </div>

          {/* Uwagi - notatka na karcie kontrahenta */}
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={labelStyle}>📝 Uwagi</label>
            <textarea
              style={{ ...inputStyle, minHeight: '48px', resize: 'vertical' }}
              value={customer.uwagi || ''}
              onChange={e => setCustomer({ ...customer, uwagi: e.target.value })}
              placeholder="Notatka wewnętrzna (nie trafia na dokumenty)"
            />
          </div>

          {/* Karty lojalnościowe */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <div>
              <label style={labelStyle}>🎫 Karta lojalnościowa 1</label>
              <input style={inputStyle} value={customer.karta_lojalnosciowa_1 || ''}
                onChange={e => setCustomer({...customer, karta_lojalnosciowa_1: e.target.value})}
                placeholder="Kod karty" />
            </div>
            <div>
              <label style={labelStyle}>🎫 Karta lojalnościowa 2</label>
              <input style={inputStyle} value={customer.karta_lojalnosciowa_2 || ''}
                onChange={e => setCustomer({...customer, karta_lojalnosciowa_2: e.target.value})}
                placeholder="Kod karty" />
            </div>
          </div>

          {/* Sekcja: Inny adres wysyłki */}
          <div style={{
            marginBottom: '0.75rem', padding: '0.5rem',
            backgroundColor: 'var(--bg)', borderRadius: '4px', border: '1px solid var(--border)'
          }}>
            <div
              onClick={() => setCustomer({...customer, wysylka_inna: !customer.wysylka_inna})}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                cursor: 'pointer', fontSize: '11px', fontWeight: '500', color: 'var(--text-secondary)'
              }}
            >
              <input
                type="checkbox"
                checked={customer.wysylka_inna || false}
                onChange={e => {
                  e.stopPropagation();
                  setCustomer({...customer, wysylka_inna: e.target.checked});
                }}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              <span>📦 Inny adres wysyłki</span>
            </div>

            {customer.wysylka_inna && (
              <div style={{ marginTop: '0.5rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <div>
                    <label style={labelStyle}>Imię odbiorcy</label>
                    <input style={inputStyle} value={customer.wysylka_imie || ''}
                      onChange={e => setCustomer({...customer, wysylka_imie: e.target.value})}
                      placeholder="(opcjonalnie)" />
                  </div>
                  <div>
                    <label style={labelStyle}>Nazwisko odbiorcy</label>
                    <input style={inputStyle} value={customer.wysylka_nazwisko || ''}
                      onChange={e => setCustomer({...customer, wysylka_nazwisko: e.target.value})}
                      placeholder="(opcjonalnie)" />
                  </div>
                </div>

                <div style={{ marginBottom: '0.5rem' }}>
                  <label style={labelStyle}>Nazwa firmy (jeśli dotyczy)</label>
                  <input style={inputStyle} value={customer.wysylka_firma || ''}
                    onChange={e => setCustomer({...customer, wysylka_firma: e.target.value})}
                    placeholder="(opcjonalnie)" />
                </div>

                <div style={{ marginBottom: '0.5rem' }}>
                  <label style={labelStyle}>📍 Adres wysyłki *</label>
                  <input style={inputStyle} value={customer.wysylka_ulica || ''}
                    onChange={e => setCustomer({...customer, wysylka_ulica: e.target.value})}
                    placeholder="Ulica i numer" required={customer.wysylka_inna} />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <div>
                    <label style={labelStyle}>Miasto *</label>
                    <input style={inputStyle} value={customer.wysylka_miasto || ''}
                      onChange={e => setCustomer({...customer, wysylka_miasto: e.target.value})}
                      required={customer.wysylka_inna} />
                  </div>
                  <div>
                    <label style={labelStyle}>Kod pocztowy *</label>
                    <input style={inputStyle} value={customer.wysylka_kod_pocztowy || ''}
                      onChange={e => setCustomer({...customer, wysylka_kod_pocztowy: e.target.value})}
                      placeholder="00-000" required={customer.wysylka_inna} />
                  </div>
                </div>

                <div style={{ marginBottom: '0.5rem' }}>
                  <label style={labelStyle}>🌍 Kraj wysyłki</label>
                  <select
                    style={inputStyle}
                    value={customer.wysylka_kraj || 'Polska'}
                    onChange={e => {
                      const selectedCountry = COUNTRIES.find(c => c.name === e.target.value);
                      setCustomer({
                        ...customer,
                        wysylka_kraj: e.target.value,
                        wysylka_kraj_kod: selectedCountry?.code || 'PL',
                        wysylka_telefon_kod: selectedCountry?.phoneCode || '+48'
                      });
                    }}
                  >
                    {COUNTRIES.map(c => (
                      <option key={c.code} value={c.name}>{c.flag} {c.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={labelStyle}>📱 Telefon kontaktowy</label>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <select
                      style={{ ...inputStyle, width: '90px', flexShrink: 0 }}
                      value={customer.wysylka_telefon_kod || '+48'}
                      onChange={e => setCustomer({...customer, wysylka_telefon_kod: e.target.value})}
                    >
                      {COUNTRIES.map(c => (
                        <option key={c.code} value={c.phoneCode}>{c.flag} {c.phoneCode}</option>
                      ))}
                    </select>
                    <input
                      style={{ ...inputStyle, flex: 1 }}
                      value={(customer.wysylka_telefon || '').replace(/^\+\d+/, '')}
                      onChange={e => setCustomer({...customer, wysylka_telefon: e.target.value.replace(/[^\d]/g, '')})}
                      placeholder="123456789"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </form>
    </BaseModal>
  );
};

export default CustomerModal;
